/* Vista Portfolio — letterboxed magazine plates with coordinate
   stamps. Each plate is a wide aspect (16:9) cover with location
   metadata. Mosaic-magazine vibe. */

import { useEffect } from "react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { getFocalStyle } from "@/lib/focal";
import { optimizeImageUrl } from "@/lib/imageOptim";
import { EditorialSkeleton } from "../editorial/_EditorialSkeleton";

const COORDS = [
  "40°43′N · 74°00′W", "34°03′N · 118°15′W", "48°51′N · 02°21′E",
  "35°41′N · 139°41′E", "51°30′N · 00°07′W", "37°48′S · 175°16′E",
  "55°45′N · 37°37′E", "01°17′N · 103°51′E",
];

export default function VistaPortfolio() {
  const { settings, loading } = useSiteSettings();
  const port = settings?.portfolio_images || [];
  const focals = settings?.portfolio_focal_points || [];
  const studioName = settings?.studio_name || "Studio";
  useEffect(() => { document.title = `${studioName} — Plates`; }, [studioName]);
  if (loading && !settings) return <EditorialSkeleton eyebrow="Loading plates." />;

  return (
    <div data-testid="vista-portfolio" style={{ background: "var(--tenant-bg, #F8F9FB)", color: "var(--tenant-fg, #102A33)" }}>
      <section className="px-6 lg:px-12 pt-16 pb-10">
        <div className="max-w-[1480px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-3">
            <div className="text-[9px] uppercase tracking-[0.42em] mb-3" style={{ color: "var(--tenant-accent)" }}>
              Field Plates · {new Date().getFullYear()}
            </div>
            <div className="text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--tenant-fg-soft)" }}>
              Archive · Issues 01–∞
            </div>
          </div>
          <div className="md:col-span-9">
            <h1 className="leading-[0.95]" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(2.5rem, 6vw, 4.5rem)" }}>
              {settings?.portfolio_title || "Where we have been."}
            </h1>
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12 pb-32 max-w-[1480px] mx-auto space-y-16">
        {port.map((src, i) => (
          <figure key={i} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
            <div className="md:col-span-2 md:pt-3">
              <div className="text-[9px] uppercase tracking-[0.42em]" style={{ color: "var(--tenant-accent)" }}>
                Plate №{String(i + 1).padStart(2, "0")}
              </div>
              <div className="text-[9px] uppercase tracking-[0.32em] mt-1" style={{ color: "var(--tenant-fg-soft)" }}>
                {COORDS[i % COORDS.length]}
              </div>
            </div>
            <div className="md:col-span-10">
              <img src={optimizeImageUrl(src, { w: 2000 })} alt=""
                className="w-full aspect-[21/9] object-cover"
                style={getFocalStyle(focals, i)}
                loading={i < 2 ? "eager" : "lazy"} decoding="async" />
            </div>
          </figure>
        ))}
        {port.length === 0 && (
          <p className="text-center text-sm uppercase tracking-[0.32em]" style={{ color: "var(--tenant-fg-soft)" }}>
            No expeditions logged yet. New plates incoming.
          </p>
        )}
      </div>
    </div>
  );
}
